/**
 * Single resolver for share media URLs.
 * - CDN / static VISION mode: `{mediaBaseUrl}/{mediaId}` (bootstrap sets mediaBaseUrl)
 * - Next local prove (legacy): `/api/media/{shareId}/{path}`
 */
import type { PublicShareManifest, SnapshotMediaRef } from '@containd/shared';

let mediaBaseUrlOverride: string | null = null;

/** Set from `window.__CONTAIND_SHARE__.mediaBaseUrl` (or tests). */
export function setShareMediaBaseUrl(url: string | null | undefined): void {
  if (!url || !String(url).trim()) {
    mediaBaseUrlOverride = null;
    return;
  }
  mediaBaseUrlOverride = String(url).replace(/\/+$/, '');
}

export function getShareMediaBaseUrl(): string | null {
  return mediaBaseUrlOverride;
}

/** Build CDN media base: `/m/{shareId}/v{version}` */
export function buildShareMediaBaseUrl(shareId: string, version: number): string {
  return `/m/${encodeURIComponent(shareId)}/v${version}`;
}

export function isAlreadyResolvedMediaUrl(value: string): boolean {
  const s = value.trim();
  if (!s) return true;
  if (s.startsWith('data:') || s.startsWith('<') || s.startsWith('blob:')) return true;
  if (s.startsWith('http://') || s.startsWith('https://')) return true;
  if (s.startsWith('/api/media/')) return true;
  if (s.startsWith('/m/')) return true;
  return false;
}

function findMedia(
  manifest: PublicShareManifest,
  mediaIdOrRef: string | SnapshotMediaRef,
): SnapshotMediaRef | null {
  if (typeof mediaIdOrRef !== 'string') return mediaIdOrRef;
  return manifest.media.find((m) => m.id === mediaIdOrRef) ?? null;
}

/**
 * Resolve a media id or ref to a browser URL.
 * Prefers CDN base when set; otherwise Next fixture API path.
 */
export function resolveShareMediaUrl(
  manifest: PublicShareManifest,
  mediaIdOrRef: string | SnapshotMediaRef | undefined | null,
): string | null {
  if (mediaIdOrRef == null || mediaIdOrRef === '') return null;
  const media = findMedia(manifest, mediaIdOrRef);
  if (!media) return null;

  const base = getShareMediaBaseUrl();
  if (base) {
    return `${base}/${encodeURIComponent(media.id)}`;
  }
  // Legacy Next prove path (fixtures served by path)
  return `/api/media/${manifest.shareId}/${media.path}`;
}

/** Find a media ref by storage path (baked descriptor strings). */
export function findShareMediaByPath(
  manifest: PublicShareManifest,
  mediaPath: string,
): SnapshotMediaRef | null {
  if (!mediaPath || isAlreadyResolvedMediaUrl(mediaPath)) return null;
  const rel = normalizeRel(mediaPath);
  return (
    manifest.media.find((m) => m.path === mediaPath || m.path === rel) ||
    manifest.media.find(
      (m) =>
        normalizeRel(m.path) === rel ||
        m.path.endsWith(`/${rel}`) ||
        rel.endsWith(m.path),
    ) ||
    null
  );
}

/** Find a media ref by id, then by storage path. */
export function findShareMedia(
  manifest: PublicShareManifest,
  mediaIdOrPath: string,
): SnapshotMediaRef | null {
  if (!mediaIdOrPath) return null;
  return findMedia(manifest, mediaIdOrPath) ?? findShareMediaByPath(manifest, mediaIdOrPath);
}

/** Resolve by storage path (baked descriptor strings). */
export function resolveShareMediaUrlByPath(
  manifest: PublicShareManifest,
  mediaPath: string,
): string | null {
  if (!mediaPath || isAlreadyResolvedMediaUrl(mediaPath)) return mediaPath || null;
  const hit = findShareMediaByPath(manifest, mediaPath);
  if (!hit) return null;
  return resolveShareMediaUrl(manifest, hit);
}

export function normalizeRel(uri: string): string {
  let s = uri.trim().replace(/\\/g, '/');
  const marker = 'ContaindImages/';
  const idx = s.indexOf(marker);
  if (idx >= 0) s = s.slice(idx + marker.length);
  s = s.replace(/^\/+/, '').replace(/^images\//, '');
  return s;
}

export function looksLikeMediaPath(value: string): boolean {
  const s = value.trim();
  if (!s || isAlreadyResolvedMediaUrl(s)) return false;
  return (
    s.includes('/') ||
    s.includes('ContaindImages') ||
    /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(s)
  );
}
